import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FaSpinner } from "../utils/icons";
import { useChangePassword } from "../hooks/useAuthMutations";
import { Button } from "@/design-system/components/react";

const ChangePassword: React.FC = () => {
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword1: "",
    newPassword2: "",
  });
  const [success, setSuccess] = useState(false);
  const { userId, token } = useAuth();
  const navigate = useNavigate();

  // TanStack Query mutation handles loading/error states
  const changePasswordMutation = useChangePassword(token, userId);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    changePasswordMutation.mutate(passwords, {
      onSuccess: () => {
        setSuccess(true);
        setPasswords({
          currentPassword: "",
          newPassword1: "",
          newPassword2: "",
        });
        // Redirect after successful password change
        setTimeout(() => {
          navigate("/worklist");
        }, 2000);
      },
    });
  };

  // Derive error message from mutation state
  const error = changePasswordMutation.error?.message ?? null;
  const isLoading = changePasswordMutation.isPending;

  return (
    <div className="flex min-h-screen bg-bg-2 text-white">
      <div className="flex-1 flex items-center justify-center p-4">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md bg-surface-2 backdrop-blur-md border border-border-strong rounded-lg p-8 shadow-2xl"
        >
          <div className="text-center mb-8">
            <h2 className="text-xl font-medium text-fg-muted">Change Password</h2>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-warn-tint border border-warn rounded-lg">
              <p className="text-sm text-warn">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-3 bg-signal-tint border border-signal-stroke rounded-lg">
              <p className="text-sm text-signal-text-tint">
                Password successfully changed! Redirecting...
              </p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="text-sm font-medium text-fg-muted mb-1 block">
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={passwords.currentPassword}
                onChange={handleInputChange}
                className="w-full bg-bg-2 border border-border-strong rounded-lg px-3 py-2
                  text-sm text-white placeholder-fg-dim
                  focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary
                  transition-colors"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-fg-muted mb-1 block">New Password</label>
              <input
                type="password"
                name="newPassword1"
                value={passwords.newPassword1}
                onChange={handleInputChange}
                className="w-full bg-bg-2 border border-border-strong rounded-lg px-3 py-2
                  text-sm text-white placeholder-fg-dim
                  focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary
                  transition-colors"
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium text-fg-muted mb-1 block">
                Confirm New Password
              </label>
              <input
                type="password"
                name="newPassword2"
                value={passwords.newPassword2}
                onChange={handleInputChange}
                className="w-full bg-bg-2 border border-border-strong rounded-lg px-3 py-2
                  text-sm text-white placeholder-fg-dim
                  focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary
                  transition-colors"
                required
              />
            </div>

            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin mr-2 h-4 w-4" />
                  <span>Changing password...</span>
                </>
              ) : (
                <span>Change Password</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
